/**
 * RODO / cookie consent - logika współdzielona (izomorficzna).
 *
 * Czysty moduł bez DOM, `next/headers` ani `server-only` - importowalny zarówno
 * z Server Components (`RodoConsent`) jak i Client Components (`CookieConsent`,
 * `consentClient`). Trzyma kontrakt cookie zgody, parsowanie/serializację stanu
 * oraz resolvery (jakie kategorie zaakceptowane, jakie typy Google Consent Mode
 * przejść na 'granted').
 *
 * Stan zgody jest persistowany w cookie czytanym server-side (Next `cookies()`)
 * - dzięki temu PIERWSZY render wie czy banner był już zatwierdzony i nie ma
 * flash'a bannera dla powracających odwiedzających.
 *
 * @see ./consentClient.ts - klienckie read/write cookie + injekcja head/body
 * @see ../../components/consent/RodoConsent.tsx - server-side apply + render
 */

import type { RodoCategory } from "@/lib/api/cms-config";

/** Nazwa cookie przechowującego stan zgody. Czytany przez Next `cookies()`. */
export const CONSENT_COOKIE = "cookie_consent";

/** Żywotność cookie zgody - 1 rok (sekundy). */
export const CONSENT_MAX_AGE = 60 * 60 * 24 * 365;

/**
 * Standardowy zestaw typów Google Consent Mode ustawianych na 'denied' w
 * default'cie - zanim odwiedzający cokolwiek zaakceptuje. Po akceptacji
 * wybrane typy przechodzą na 'granted' przez `gtag('consent','update')`.
 */
export const CONSENT_MODE_TYPES = [
  "ad_storage",
  "ad_user_data",
  "ad_personalization",
  "analytics_storage",
  "functionality_storage",
  "personalization_storage",
  "security_storage",
] as const;

/**
 * Stan zgody zapisany w cookie.
 * - `all`  - odwiedzający zaakceptował wszystkie kategorie (Akceptuję wszystkie).
 * - `ids`  - identyfikatory zaakceptowanych kategorii (wybór częściowy). Zawsze
 *            zawiera kategorie `always_active` (niezbędne).
 */
export type ConsentState = {
  v: 1;
  all: boolean;
  ids: number[];
};

/**
 * Parsuje surową wartość cookie do `ConsentState`. Zwraca `null` gdy cookie
 * brak lub jest uszkodzony (= brak zgody, banner powinien się pokazać).
 *
 * Cookie zapisywany jest przez `encodeURIComponent` (JSON zawiera przecinki i
 * `{}` niedozwolone w surowej wartości cookie). Next `cookies()` NIE dekoduje
 * automatycznie, więc dekodujemy tutaj defensywnie (decode stringu bez `%` to
 * no-op, więc bezpieczne też dla wartości klienckiej już zdekodowanej).
 */
export function parseConsent(raw: string | undefined | null): ConsentState | null {
  if (!raw) return null;
  let str = raw;
  try {
    str = decodeURIComponent(raw);
  } catch {
    /* Niepoprawny escape - próbujemy parsować oryginał. */
  }
  try {
    const obj = JSON.parse(str) as unknown;
    if (!obj || typeof obj !== "object") return null;
    const rec = obj as Record<string, unknown>;
    const ids = Array.isArray(rec.ids)
      ? rec.ids.filter((x): x is number => typeof x === "number")
      : [];
    return { v: 1, all: rec.all === true, ids };
  } catch {
    return null;
  }
}

/** Serializuje `ConsentState` do stringu cookie. */
export function serializeConsent(state: ConsentState): string {
  return JSON.stringify({ v: 1, all: state.all, ids: state.ids });
}

/** Type guard - czy zgoda została udzielona (cookie obecny i poprawny). */
export function hasConsent(state: ConsentState | null): state is ConsentState {
  return state !== null;
}

/**
 * Kategorie zaakceptowane dla danego stanu.
 * - brak stanu  -> tylko `always_active` (niezbędne),
 * - `all=true`  -> wszystkie,
 * - inaczej     -> kategorie z `ids` + `always_active`.
 */
export function acceptedCategories(
  categories: RodoCategory[],
  state: ConsentState | null,
): RodoCategory[] {
  if (!state) return categories.filter((c) => c.always_active);
  if (state.all) return categories;
  const idSet = new Set(state.ids);
  return categories.filter((c) => c.always_active || idSet.has(c.id));
}

/**
 * Typy Google Consent Mode do ustawienia na 'granted' dla zaakceptowanych
 * kategorii. Sanityzacja `[a-z_]` - wartości pochodzą z DB (admin-entered),
 * a trafiają do inline `<script>`, więc odsiewamy wszystko poza znanym formatem.
 */
export function grantedTypes(
  categories: RodoCategory[],
  state: ConsentState | null,
): string[] {
  const types = new Set<string>();
  for (const c of acceptedCategories(categories, state)) {
    for (const t of c.consent_types ?? []) {
      if (/^[a-z_]+$/i.test(t)) types.add(t);
    }
  }
  return [...types];
}

/**
 * Buduje skrypt Google Consent Mode (inline, `beforeInteractive`):
 *   1. definiuje `dataLayer` + `gtag`,
 *   2. `gtag('consent','default', { ... 'denied', wait_for_update:1000 })`,
 *   3. opcjonalnie `gtag('consent','update', { ...granted })` gdy odwiedzający
 *      ma już zapisaną zgodę (re-apply przy każdym wejściu).
 *
 * Default(denied) MUSI poprzedzać skrypty analityczne (GTM z `head_html` ładują
 * się `afterInteractive`), żeby tagi czekały na zgodę (`wait_for_update`).
 *
 * @param granted - typy do 'granted' (puste = sam default denied)
 */
export function buildConsentModeScript(granted: string[]): string {
  const denied = CONSENT_MODE_TYPES.map((t) => `'${t}':'denied'`).join(",");
  let script =
    "window.dataLayer=window.dataLayer||[];" +
    "function gtag(){dataLayer.push(arguments);}" +
    `gtag('consent','default',{${denied},'wait_for_update':1000});`;

  const safe = granted.filter((t) => /^[a-z_]+$/i.test(t));
  if (safe.length) {
    const grantedObj = safe.map((t) => `'${t}':'granted'`).join(",");
    script += `gtag('consent','update',{${grantedObj}});`;
  }
  return script;
}
